import { Injectable } from '@jack-henry/frontend-utils/di';
import { WindowService } from '@treasury/utils';
import 'signalr/jquery.signalR';
import { ConfigurationService } from '../config';
import { RealtimeService } from './realtime.service';

const DEFAULT_RETRIES = 5;

/**
 * @deprecated Use `NetCoreRealtimeService` once the API has migrated completely.
 *
 * A service for interacting with live messages and events from the TM API in realtime.
 * Compatible with ASP.net classic SignalR implementation.
 */
@Injectable()
export class LegacyRealtimeService extends RealtimeService {
    constructor(
        config: ConfigurationService,
        private window: WindowService
    ) {
        super(config);
        this.connected$.subscribe(isConnected => {
            if (isConnected) {
                this.retryAttempts = 0;
                this.attemptingReconnect = false;
            } else if (this.autoReconnect) {
                this.reconnect();
            }
        });
    }

    private connection?: SignalR.Hub.Connection;

    private proxy?: SignalR.Hub.Proxy;

    private autoReconnect = false;

    /**
     * The maximum number of retries the service will attempt on a broken connection.
     */
    private maxRetries = DEFAULT_RETRIES;

    private retryAttempts = 0;

    private attemptingReconnect = false;

    public async init(useLogging = false) {
        if (this.proxy || this.connection) {
            await this.disconnect();
        }

        this.connection = $.hubConnection(this.endpoint, {
            useDefaultPath: false,
            logging: useLogging,
        });
        this.proxy = this.connection.createHubProxy('pushNotificationHub');
    }

    public async disconnect() {
        if (!this.connection || !this.proxy) {
            throw RealtimeService.createInitError('Could not stop listening for real-time events.');
        }

        this.topics.forEach(t => this.proxy?.off(t));
        this.topics = [];
        this.connection.stop();
        this.connection = undefined;
        this.proxy = undefined;
    }

    /**
     * Subscribe to a `topic` and open a connection, if one hasn't already been established.
     */
    public async subscribe<T>(topic: string, callback: (message: T) => void) {
        if (!this.proxy || !this.connection) {
            throw RealtimeService.createInitError(`Could not stop subscribe to topic "${topic}"`);
        }

        this.proxy.on(topic, callback);
        this.topics.push(topic);

        if (!this.isConnected && !this.attemptingReconnect) {
            await this.connect();
        }

        return {
            unsubscribe: () => {
                this.unsubscribe(topic, callback);
            },
        };
    }

    public publish(topic: string, ...args: unknown[]) {
        if (!this.proxy) {
            throw RealtimeService.createInitError(`Could not publish a message for "${topic}"`);
        }

        return new Promise<void>((resolve, reject) => {
            this.proxy
                ?.invoke(topic, ...args)
                .then(resolve)
                .catch(reject);
        });
    }

    /**
     * Begin listening for realtime events.
     *
     * In order for this method to succeed, at least one topic must have been subscribed to.
     */
    private async connect(autoReconnect: true, maxRetries?: number): Promise<void>;

    private async connect(autoReconnect?: boolean): Promise<void>;

    private async connect(autoReconnect = true, maxRetries = DEFAULT_RETRIES) {
        if (this.isConnected) {
            return Promise.resolve();
        }

        if (!this.proxy || !this.connection) {
            throw RealtimeService.createInitError(
                'Could not begin listening for real-time events.'
            );
        }

        this.autoReconnect = autoReconnect;
        this.maxRetries = maxRetries;

        if (!this.proxy.hasSubscriptions()) {
            // at least one listener must be present in order to start a new connection
            throw new Error(
                'Cannot call connect() before at least one topic has been subscribed to.'
            );
        }

        return new Promise<void>((resolve, reject) => {
            this.connection
                ?.disconnected(() => {
                    this.connectionSource.emit(false);
                })
                .start(() => {
                    this.connectionSource.emit(true);
                })
                .done(resolve)
                .fail(reject);
        });
    }

    private unsubscribe<T>(topic: string, callback: (message: T) => void) {
        if (!this.proxy) {
            throw RealtimeService.createInitError(
                `Could not stop unsubscribe from topic "${topic}".`
            );
        }

        this.topics = this.topics.filter(t => t !== topic);
        this.proxy.off(topic, callback);
    }

    private async reconnect() {
        if (this.isConnected || this.attemptingReconnect) {
            return;
        }

        this.attemptingReconnect = true;

        while (this.retryAttempts < this.maxRetries && !this.isConnected) {
            this.retryAttempts++;

            await this.window.createTimer(RealtimeService.RETRY_THRESHOLD);

            await this.connect(this.autoReconnect);
        }

        this.attemptingReconnect = false;

        if (this.retryAttempts >= this.maxRetries && !this.isConnected) {
            throw new Error(`Failed to reconnect to SignlarR after ${this.retryAttempts} attempts`);
        }
    }
}
